import { useLocation, useParams } from "react-router-dom";
import { useEffect, useRef, useState, lazy, Suspense } from "react";
import axios from "axios";
import Sidebar from "../Panels/AdminPanel/Components/Sidebar";
import Header from "../Panels/Components/Header";
import Dashboard from "../Panels/AdminPanel/Dashboard";
import WineManagement from "../Panels/AdminPanel/WinesManagement/WineManagement";
import RestaurantManagement from "../Panels/AdminPanel/RestaurantManagement/RestaurantManagement";
import WineListManagement from "../Panels/RestaurantPanel/WineListManagement/WineListManagement";
import StockView from "../Panels/RestaurantPanel/StockManagement/StockView";
import UserManagement from "../Panels/AdminPanel/UserManagement/UserManagement";
import UserAdd from "../Panels/AdminPanel/UserManagement/UserAdd";
import UserRoleManagement from "../Panels/AdminPanel/UserRoleManagement/UserRoleManagement";
import Settings from "../Panels/AdminPanel/Settings";
import ChangePassword from "../Panels/Components/ChangePassword";
import AllOrderManagement from "../Panels/AdminPanel/AllOrderManagement/AllOrderManagement";
import AllergensManagement from "../Panels/AdminPanel/AllergensManagement/AllergensManagement";

// Lazy load PageNotFound
const PageNotFound = lazy(() => import("../Pages/PageNotFound"));

const AdminRoutes = () => {
  const { admin_name, slug2 } = useParams();
  const location = useLocation();
  const isUserRoute = location.pathname.startsWith("/restaurant/");
  const showSidebar = !isUserRoute && location.pathname !== "/admin/login";
  const [role, setRole] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [showSidebarForMobile, setShowSidebarForMobile] = useState(false);
  const [checkPermission, setCheckPermission] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  const [logo, setLogo] = useState(null);
  const [userName, setUserName] = useState("");
  const [userType, setUserType] = useState(() => localStorage.getItem("Role") || "");
  const [userID, setUserID] = useState(() => localStorage.getItem("loginUserId") || "");
  const [roleName, setRoleName] = useState('')

  const logoUrl = import.meta.env.VITE_BACKEND_APP_STORAGE_URL;

  useEffect(() => {
    const storedRole = localStorage.getItem("Role");
    if (storedRole === "admin") {
      setRole(storedRole);
      setShowAlert(true);
    }
  }, [location.pathname]);

  const dashboardRef = useRef();
  const wineRef = useRef();
  const restaurantRef = useRef();
  const userRef = useRef();
  const allergensRef = useRef();
  const orderRef = useRef();
  const userRoleRef = useRef();
  const dropdownRef = useRef();

  const dashboard = () => {
    if (dashboardRef.current) {
      dashboardRef.current.dashboard();
    }
  };
  const wine = () => {
    if (wineRef.current) {
      wineRef.current.wine();
    }
  };
  const restaurant = () => {
    if (restaurantRef.current) {
      restaurantRef.current.restaurant();
    }
  };
  const user = () => {
    if (userRef.current) {
      userRef.current.user();
    }
  };
  const allergens = () => {
    if (allergensRef.current) {
      allergensRef.current.allergens();
    }
  };
  const order = () => {
    if (orderRef.current) {
      orderRef.current.order();
    }
  };
  const userRole = () => {
    if (userRoleRef.current) {
      userRoleRef.current.userRole();
    }
  };
  const dropdown = () => {
    if (dropdownRef.current) {
      dropdownRef.current.dropdown();
    }
  };

  const closeModal = () => {
    dashboard();
    wine();
    restaurant();
    user();
    allergens();
    order();
    userRole();
    setShowSidebarForMobile(false);
  }

  const hasPermission = (moduleName, action) => {
    const module = checkPermission?.find(item => item.module === moduleName);
    if (!module) {
      // console.error(`Module '${moduleName}' not found.`);
      return false;
    }
    return module.actions[action] || false;
  };

  const getPermissions = async () => {
    try {
      const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
      const response = await axios.get(
        `${apiUrl}/api/v1/roles/${localStorage.getItem("RoleId")}/show-permissions`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setCheckPermission(response?.data?.permissions);
      // console.log(response?.data?.permissions, "All Permissions");
    } catch (error) {
      console.error("Error fetching user role data:", error);
    } finally {
      setLoadingPermissions(false);
    }
  };

  useEffect(() => {
    getPermissions();
  }, [slug2, admin_name]);

  useEffect(() => {
    const role = localStorage.getItem("Role");
    const roleId = localStorage.getItem("loginUserId");
    setUserType(role);
    setUserID(roleId);
    const role_name = localStorage.getItem("RoleName")
    setRoleName(role_name)
  }, []);

  const fetchAndUpdateLogo = async () => {
    try {
      const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
      const token = localStorage.getItem("token")
      if (userType === "admin") {
        const responseProfileData = await axios.get(`${apiUrl}/api/v1/users/${userID}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // console.log(responseProfileData, responseProfileData)
        const completeLogoUrl = responseProfileData?.data?.avatar
          ? `${logoUrl}/${responseProfileData?.data?.avatar}`
          : null;

        setLogo(completeLogoUrl);
        setUserName(responseProfileData?.data?.name || "");
      }
    } catch (error) {
      console.error('Error fetching Profile Settings data:', error);
    }
  };

  useEffect(() => {
    fetchAndUpdateLogo();
  }, [userType, userID, roleName]);

  return (
    loadingPermissions ? (
      <div className="flex justify-center items-center h-screen" >
        <svg
          aria-hidden="true"
          className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-[#8B2433]"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="sr-only">Loading...</span>
      </div>
    ) : (
      <div className="flex bg-[#FFFFFF] h-screen w-full gap-[2%] sm:p-5 px-0">
        {/* Alert Message */}

        {/* {role && showAlert && (
          <div
            className="sm:top-0 top-[60px] py-1 text-xs w-[210px] sm:w-[270px] sm:text-center text-center absolute translate-x-[-50%] left-[50%] mt-3 p-2 sm:text-sm text-blue-800 rounded-lg bg-blue-50 poppins-regular z-0"
            role="alert"
          >
            You are in
            <span className="font-medium poppins-regular"> Admin Console!</span>
          </div>
        )} */}
        {showSidebar && (
          <Sidebar
            showSidebarForMobile={showSidebarForMobile}
            setShowSidebarForMobile={setShowSidebarForMobile}
            hasPermission={hasPermission}
            ref={dropdownRef}
            closeModal={closeModal}
          />
        )}
        <div className="flex flex-col w-full h-full overflow-y-scroll p-5">
          <Header
            showSidebarForMobile={showSidebarForMobile}
            setShowSidebarForMobile={setShowSidebarForMobile}
            logo={logo}
            userName={userName}
          />
          <Suspense
            fallback={
              <div
                role="status"
                className="flex my-20 justify-center items-center h-full"
              >
                <svg
                  aria-hidden="true"
                  className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-[#8B2433]"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                <span className="sr-only">Loading...</span>
              </div>
            }
          >
            {!slug2 && admin_name === "dashboard" && (hasPermission("Dashboard", "view") ? <Dashboard ref={dashboardRef} hasPermission={hasPermission} /> : <PageNotFound />)}
            {!slug2 && admin_name === "wine" && (hasPermission("Wines", "view") ? <WineManagement ref={wineRef} hasPermission={hasPermission} /> : <PageNotFound />)}
            {!slug2 && admin_name === "restaurantManagement" && (hasPermission("Restaurants", "view") ? <RestaurantManagement ref={restaurantRef} hasPermission={hasPermission} /> : <PageNotFound />)}
            {slug2 && admin_name === "restaurantManagement" && (hasPermission("WineList", "view") ? <WineListManagement hasPermission={hasPermission} /> : <PageNotFound />)}
            {slug2 && admin_name === "wineStock" && (hasPermission("WineStock", "view") ? <StockView hasPermission={hasPermission} /> : <PageNotFound />)}
            {!slug2 && admin_name === "userManagement" && (hasPermission("Users", "view") ? <UserManagement ref={userRef} hasPermission={hasPermission} /> : <PageNotFound />)}
            {!slug2 && admin_name === "user-add" && (hasPermission("Users", "add") ? <UserAdd /> : <PageNotFound />)}
            {!slug2 && admin_name === "userRole" && (hasPermission("Roles", "view") ? <UserRoleManagement ref={userRoleRef} hasPermission={hasPermission} /> : <PageNotFound />)}
            {!slug2 && admin_name === "settings" && <Settings dropdown={dropdown} refreshLogo={fetchAndUpdateLogo} />}
            {!slug2 && admin_name === "changePassword" && <ChangePassword dropdown={dropdown} />}

            {!slug2 && admin_name === "orderManagement" && <AllOrderManagement ref={orderRef} />}
            {!slug2 && admin_name === "allergensManagement" && <AllergensManagement ref={allergensRef} />}

          </Suspense>
        </div>
      </div>
    )
  );
};

export default AdminRoutes;